import { MILLION_SEED_PACKAGES, QUANTITY_ROUNDINGS, SIGNAL_MAX_DATE } from 'appConstants';
import type { ITradingSeason } from 'contexts/SeedMetadata';
import type { ISeedProduct, ISeedProductSubgroup, ITreatment } from 'contexts/SeedProducts';
import dayjs, { type Dayjs } from 'dayjs';

export const calculateSeedRate = (seedNumber, tgw, germination, unit) => {
  const mappedUnit = unit === 'ha' ? 1 : 2.47105;
  const result = (seedNumber * tgw) / (germination / 100) / 100 / mappedUnit;
  return Number(result.toFixed(2));
};

export const calculateSeedRequired = (seedRate, area) => {
  const result = (seedRate * area) / 1000;
  return Number(result.toFixed(2));
};

export const formatTgwNotes = (area, unit, seedNumber, germination) => {
  const germinationText = `G${germination}%`;
  return `TGW${area}${unit}@${seedNumber}${germinationText}`;
};

export const roundQuantityBasedOnReference = (quantity, reference) => {
  if (!QUANTITY_ROUNDINGS[reference]) return quantity;
  return QUANTITY_ROUNDINGS[reference](quantity);
};

export const getMaxPayDate = (season: ITradingSeason, crop: ISeedProductSubgroup): Dayjs => {
  const seasonStartMon = Number(dayjs(season.seasonStartDate).month());
  const [cropPayDay, cropPayMonth] = crop.delPayDate.split('/');

  let checkYear = dayjs(season.seasonEndDate).year();
  const checkPayMonth = Number(cropPayMonth);

  if (checkPayMonth > seasonStartMon) {
    checkYear = dayjs(season.seasonStartDate).year();
  }

  const d = new Date(Number(checkYear), checkPayMonth - 1, Number(cropPayDay));
  return dayjs(d);
};

export const getBagSizeType = (uomDescription): string =>
  uomDescription ? ` (${uomDescription})` : '';

export const dateIsBeforeMaxDateForSignal = (season: ITradingSeason) => {
  const seasonEndYear = dayjs(season.seasonEndDate).year();
  const maxDate = dayjs(`${seasonEndYear}-${SIGNAL_MAX_DATE.MONTH}-${SIGNAL_MAX_DATE.DAY}`);
  return dayjs().isSameOrBefore(maxDate);
};

export const createTreatmentsFromData = (
  currentSubgroup: ISeedProductSubgroup,
  currentSelectedProduct?: ISeedProduct,
) => {
  return formatTreatmentsForFormik(
    getTreatments(getTreatmentsForVariety(currentSubgroup, currentSelectedProduct), true),
  );
};

export const getTreatmentsForVariety = (
  currentSubgroup: ISeedProductSubgroup,
  currentSelectedProduct?: ISeedProduct,
) => {
  if (
    currentSelectedProduct &&
    Array.isArray(currentSelectedProduct.treatments) &&
    currentSelectedProduct.treatments.length > 0
  ) {
    return currentSelectedProduct.treatments;
  }

  return currentSubgroup.treatments;
};

export const formatTreatmentsForFormik = (treatments: ITreatment[]) =>
  treatments.reduce((acc: { treatmentReference: string }[], current) => {
    return [
      ...acc,
      {
        treatmentReference: current.treatmentReference,
      },
    ];
  }, []);

export const getTreatmentsForRemove = (selectedTreatments: ITreatment[]) => {
  let treatmentsForRemove: number[] = [];

  for (const treatment of selectedTreatments) {
    if (treatment && Array.isArray(treatment.removes) && treatment.removes.length > 0) {
      treatmentsForRemove = [...treatmentsForRemove, ...treatment.removes];
    }
  }

  return treatmentsForRemove;
};

export const getTreatments = (treatments: ITreatment[], isDefault: boolean) => {
  return treatments.filter((treatment) => treatment.isDefault === isDefault);
};

export const getIndexedTreatments = (treatments: ITreatment[]) =>
  treatments.reduce((indexed: { [x: string]: any }, treatment: ITreatment) => {
    if (treatment.treatmentReference) {
      indexed[treatment.treatmentReference] = treatment;
    }
    return indexed;
  }, {});

export const isMillionSeedPackage = (packageReference: string) => {
  return MILLION_SEED_PACKAGES.includes(packageReference.toLocaleLowerCase());
};
